import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn, useFormState } from 'react-hook-form';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import CareConnectSettingsProviderContext from 'features/care-connect-settings/contexts/care-connect-settings-provider-context';
import { PersonalNumberRange } from 'features/care-connect-settings/domain/models/personal-number-range';

export type PersonalNumberRangeRow = {
  personalNumberRange: PersonalNumberRange;
  form: UseFormReturn<PersonalNumberRange>;
  submitForm: () => void;
  viewingMode: ViewingMode;
  setViewingMode: (viewingMode: ViewingMode) => void;
  startEditing: () => void;
  stopEditing: () => void;
  cancelCreate: (personalNumberRange: PersonalNumberRange) => void;
};

const usePersonalNumberRangeRow = (personalNumberRange: PersonalNumberRange, initialViewingMode: ViewingMode): PersonalNumberRangeRow => {
  const { personalNumberRangeDetails } = use(CareConnectSettingsProviderContext);
  const { upsertPersonalNumberRange, removePersonalNumberRange, setIsEditing, setShouldShowConfirmationPopup } = personalNumberRangeDetails;

  const { enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const [viewingMode, setViewingMode] = useState(initialViewingMode);

  const form = useForm<PersonalNumberRange>({
    mode: 'onSubmit',
    reValidateMode: 'onSubmit',
    defaultValues: personalNumberRange,
  });

  const { isDirty } = useFormState({ control: form.control });

  const submitForm = (): void => {
    const formData = form.getValues();

    upsertPersonalNumberRange(formData, () => {
      setShouldShowConfirmationPopup(false);
      enablePopUpGuard(false);
      setViewingMode('viewing');
    });
  };

  const startEditing = (): void => {
    setIsEditing(true);
    setViewingMode('editing');
  };

  const stopEditing = (): void => {
    setIsEditing(false);
    setViewingMode('viewing');
    form.reset(personalNumberRange);
  };

  const cancelCreate = (personalNumberRange: PersonalNumberRange): void => {
    setIsEditing(false);
    setShouldShowConfirmationPopup(false);
    enablePopUpGuard(false);
    form.reset(personalNumberRange);
    removePersonalNumberRange(personalNumberRange);
  };

  useEffect(() => {
    setShouldShowConfirmationPopup(isDirty);
    enablePopUpGuard(isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDirty]);

  useEffect(() => {
    form.reset(personalNumberRange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalNumberRange.id]);

  return {
    personalNumberRange,
    form,
    submitForm,
    viewingMode,
    setViewingMode,
    startEditing,
    stopEditing,
    cancelCreate,
  };
};

export default usePersonalNumberRangeRow;
